import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';

const ResendVerification = () => {
  const { sendVerificationEmail, currentUser, emailVerified } = useAuth();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleResend = async () => {
    try {
      setError('');
      setMessage('');
      setLoading(true);
      await sendVerificationEmail();
      setMessage('Verification email sent! Please check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  // If email is already verified, redirect
  if (emailVerified) {
    return (
      <div className="min-h-screen bg-tech-dark text-tech-text flex items-center justify-center px-4 bg-tech-mesh">
        <div className="max-w-md w-full">
          <div className="tech-card rounded-2xl p-8 border-tech-cyan/10 text-center">
            <CheckCircle className="w-12 h-12 text-tech-accent mx-auto mb-4" aria-hidden />
            <h2 className="text-2xl font-bold text-tech-text mb-2">Email Already Verified</h2>
            <p className="text-tech-text-muted mb-6">Your email is already verified!</p>
            <Link
              to="/"
              className="inline-block w-full py-3 rounded-xl font-semibold text-tech-dark hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // If not logged in, redirect to login
  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text flex items-center justify-center px-4 bg-tech-mesh">
      <div className="max-w-md w-full">
        <div className="tech-card rounded-2xl p-8 border-tech-cyan/10">
          <div className="flex justify-center mb-6">
            <Mail className="w-12 h-12 text-tech-cyan" aria-hidden />
          </div>
          <h2 className="text-2xl font-bold text-center mb-2 text-tech-text">Resend Verification Email</h2>
          <p className="text-tech-text-muted text-center mb-8">
            We&apos;ll send a verification email to <strong className="text-tech-text">{currentUser.email}</strong>
          </p>

          {error && (
            <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg flex items-center gap-2 text-amber-200" role="alert">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 bg-tech-accent/20 border border-tech-accent/50 rounded-lg flex items-center gap-2 text-tech-accent" role="status">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>{message}</span>
            </div>
          )}

          <button
            onClick={handleResend}
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-lg text-tech-dark hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed mb-4 focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
            style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
          >
            {loading ? 'Sending...' : 'Resend Verification Email'}
          </button>

          <div className="text-center space-y-2">
            <Link to="/login" className="text-tech-cyan hover:text-tech-cyan-dark text-sm">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResendVerification;




